import { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { Modal } from '@mantine/core';
import DepartmentModal from './components/DepartmentModal';
import DepartmentTable from './components/DepartmentTable';
import CompanyModal from './components/CompanyModal';
import { updateCompany } from '@mygpt/app';
import { useAdminStore } from '@/store/admin';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { Avatar } from '@mantine/core';

export default function Organization() {
  const { t } = useTranslation();
  const {
    companyId,
    departmentList,
    noneDepartmentUsers,
    fetchDepartmentByCompanyId,
    fetchCompanyId,
    fetchCompanySetting,
  } = useAdminStore((state) => state);
  const { refreshDepartmentData } = useAdminStore((state) => state);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [selectedDept, setSelectedDept] = useState<any | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [parentId, setParentId] = useState<string>('-1');

  const handleDepartmentSubmit = async (formData: any) => {
    // 使用统一的刷新方法
    try {
      await refreshDepartmentData();
      showNotification({
        message: t('adminConsole.tip.create_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to refresh department data:', error);
      showNotification({
        message: t('adminConsole.tip.create_error'),
        color: 'red',
      });
    }
  };

  const handleAddDepartment = (parentId: string) => {
    setParentId(parentId);
    setSelectedDept(null);
    setModalMode('add');
    setShowDeptModal(true);
  };

  // 处理部门编辑
  const handleEditDepartment = (dept: any) => {
    setSelectedDept(dept);
    setModalMode('edit');
    setShowDeptModal(true);
    setParentId(dept.parentId.toString());
  };

  // 处理公司信息更新
  const handleCompanySubmit = (formData: any) => {
    updateCompany({
      id: companyId,
      name: formData.name,
      description: formData.description,
      initialPassword: formData.initialPassword,
    }).then(({ data }) => {
      if (data.success) {
        showNotification({
          message: t('adminConsole.tip.update_success'),
          color: 'green',
        });
        // 因为汉得服务器没有部署组织相关服务，接口404，所以本地部署都禁用掉先。
        // fetchCompanyId();
        fetchCompanySetting({ companyId: companyId });
        setShowCompanyModal(false);
      } else {
        showNotification({
          message: t('adminConsole.tip.update_error'),
          color: 'red',
        });
      }
    });
  };

  // 添加公司编辑处理
  const handleEditCompany = () => {
    setShowCompanyModal(true);
  };

  return (
    <div className="space-y-8 p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('adminConsole.organization.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('adminConsole.organization.description')}</p>
        </div>

        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleAddDepartment('-1')}
            className="inline-flex items-center gap-2 px-4 py-2
              bg-white border border-gray-300 text-gray-700 rounded-lg
              hover:bg-gray-50 shadow-sm transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>{t('adminConsole.organization.newDepartment')}</span>
          </motion.button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{t('adminConsole.organization.structure')}</h2>
        </div>
        {Array.isArray(departmentList) && departmentList.length >= 0 ? (
          <DepartmentTable
            onAddDepartment={handleAddDepartment}
            onEditDepartment={handleEditDepartment}
            onEditCompany={handleEditCompany}
          />
        ) : (
          <p>{t('adminConsole.organization.loadingDepartments')}</p>
        )}
      </div>

      {/* 未分配成员列表 */}
      {Array.isArray(noneDepartmentUsers) && noneDepartmentUsers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{t('adminConsole.organization.noneDepartmentUsers')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('adminConsole.organization.noneDepartmentUsersDescription')}
            </p>
          </div>
          <div className="p-6">
            <div>
              {noneDepartmentUsers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-3 py-1.5 px-4 rounded-lg border-b border-gray-100 hover:bg-white transition-colors duration-150"
                >
                  <Avatar
                    src={`${import.meta.env.VITE_OGS_API_BASE_URL}/user/${member.id}/logo?timestamp=${Date.now()}`}
                    alt={member.name}
                    className="w-8 h-8 rounded-full"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '';
                      target.parentElement?.classList.add('bg-gray-200');
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{member.name}</div>
                    <div className="text-xs text-gray-500 truncate">{member.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <Modal
        opened={showDeptModal}
        onClose={() => setShowDeptModal(false)}
        title={
          modalMode === 'add'
            ? t('adminConsole.organization.addDepartment')
            : t('adminConsole.organization.editDepartment')
        }
        centered
      >
        <DepartmentModal
          isOpen={showDeptModal}
          onClose={() => setShowDeptModal(false)}
          mode={modalMode}
          department={selectedDept}
          onSubmit={handleDepartmentSubmit}
          parentId={parentId}
        />
      </Modal>

      <Modal
        opened={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        title={t('adminConsole.organization.editCompany')}
        centered
      >
        <CompanyModal
          isOpen={showCompanyModal}
          onClose={() => setShowCompanyModal(false)}
          onSubmit={handleCompanySubmit}
        />
      </Modal>
    </div>
  );
}
